import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/context/ChatContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Send, Paperclip, Loader2, FileText, Building2, Plus, X, MessageSquare, Mail } from "lucide-react";
import FollowUpEmailDialog from "@/components/chat/FollowUpEmailDialog";

function Avatar({ name, src, size = "h-8 w-8" }) {
  if (src) return <img src={src} alt="" className={`${size} shrink-0 rounded-full object-cover`} />;
  return (
    <div className={`${size} flex shrink-0 items-center justify-center rounded-full bg-secondary text-[11px] font-bold uppercase text-foreground`}>
      {(name || "U").slice(0, 2)}
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const t = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return sameDay ? t : `${d.toLocaleDateString([], { month: "short", day: "numeric" })} ${t}`;
}

function dayKey(iso) {
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toDateString();
}

function daySeparatorLabel(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const now = new Date();
  const yst = new Date(now);
  yst.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return "Today";
  if (d.toDateString() === yst.toDateString()) return "Yesterday";
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString([], sameYear
    ? { weekday: "short", month: "short", day: "numeric" }
    : { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function FollowUpCard({ a }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid={`chat-followup-${a.id}`}
        className="mt-1.5 flex max-w-xs items-center gap-2 rounded-lg border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/8 px-3 py-2 text-left transition hover:border-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/15"
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]">
          <Mail className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-[hsl(var(--success))]">Send follow-up email</div>
          <div className="truncate text-xs text-muted-foreground">"How did we do?" → {a.sub || a.label}</div>
        </div>
      </button>
      <FollowUpEmailDialog quoteId={a.id} open={open} onOpenChange={setOpen} />
    </>
  );
}

function ShareCard({ a }) {
  if (a.type === "followup") return <FollowUpCard a={a} />;
  const to = a.type === "quote" ? `/quotes/${a.id}` : `/customers/${encodeURIComponent(a.id)}`;
  const Icon = a.type === "quote" ? FileText : Building2;
  return (
    <Link
      to={to}
      data-testid={`chat-share-${a.type}-${a.id}`}
      className="mt-1.5 flex max-w-xs items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 transition hover:border-[hsl(var(--primary))]/50 hover:bg-secondary/50"
    >
      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold">{a.label || a.id}</div>
        {a.sub && <div className="truncate text-xs text-muted-foreground">{a.sub}</div>}
      </div>
    </Link>
  );
}

function renderText(text, mentionNames) {
  if (!text) return null;
  if (!mentionNames.length) return text;
  const esc = mentionNames.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`@(${esc.join("|")})`, "g");
  const out = [];
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<span key={i++} className="rounded bg-[hsl(var(--primary))]/12 px-1 font-semibold text-[hsl(var(--primary))]">{m[0]}</span>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function activeMention(value, caret) {
  const upto = value.slice(0, caret);
  const m = upto.match(/(^|\s)@(\S*)$/);
  if (!m) return null;
  return { at: caret - m[2].length - 1, query: m[2] };
}

function AttachPicker({ onAttach }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("quote");
  const [qy, setQy] = useState("");
  const [items, setItems] = useState([]);
  const timer = useRef(null);

  const search = (t, query) => {
    if (t === "quote") {
      api.get("/quotes", { params: { search: query } }).then((r) =>
        setItems((r.data || []).slice(0, 12).map((q) => ({ type: "quote", id: q.id, label: q.quote_number, sub: q.customer_name || "" }))),
      ).catch(() => setItems([]));
    } else {
      api.get("/addresses", { params: { search: query } }).then((r) => {
        const seen = new Set();
        const list = [];
        (r.data || []).forEach((a) => {
          const c = (a.company || "").trim();
          if (c && !seen.has(c.toLowerCase())) { seen.add(c.toLowerCase()); list.push({ type: "customer", id: c, label: c, sub: [a.city, a.state].filter(Boolean).join(", ") }); }
        });
        setItems(list.slice(0, 12));
      }).catch(() => setItems([]));
    }
  };

  useEffect(() => { if (open) search(tab, qy); /* eslint-disable-next-line */ }, [open, tab]);
  const onQy = (v) => { setQy(v); clearTimeout(timer.current); timer.current = setTimeout(() => search(tab, v), 300); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0" data-testid="chat-attach-btn" title="Attach a quote or customer">
          <Paperclip className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" side="top" className="w-80 p-0">
        <div className="flex border-b border-border">
          {[["quote", "Quotes"], ["customer", "Customers"]].map(([id, lbl]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`chat-attach-tab-${id}`}
              className={`flex-1 px-3 py-2 text-xs font-semibold ${tab === id ? "border-b-2 border-[hsl(var(--primary))] text-[hsl(var(--primary))]" : "text-muted-foreground"}`}>{lbl}</button>
          ))}
        </div>
        <div className="p-2">
          <Input value={qy} onChange={(e) => onQy(e.target.value)} placeholder={`Search ${tab === "quote" ? "quotes" : "customers"}…`} className="h-8" data-testid="chat-attach-search" />
          <div className="mt-2 max-h-56 overflow-y-auto">
            {items.length === 0 && <div className="py-4 text-center text-xs text-muted-foreground">Type to search.</div>}
            {items.map((it) => (
              <button key={`${it.type}-${it.id}`} onClick={() => { onAttach(it); setOpen(false); }} data-testid={`chat-attach-item-${it.id}`}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-secondary">
                {it.type === "quote" ? <FileText className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> : <Building2 className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{it.label}</span>
                  {it.sub && <span className="block truncate text-xs text-muted-foreground">{it.sub}</span>}
                </span>
              </button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function ChatThread({ conversationId, defaultAttach, onActivity, heightClass = "h-[60vh]", emptyHint }) {
  const { user } = useAuth();
  const { users, refreshUnread } = useChat();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]); // {id,name}
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [mentionState, setMentionState] = useState(null); // {at, query}
  const taRef = useRef(null);
  const scrollRef = useRef(null);
  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const scrollToBottom = () => {
    requestAnimationFrame(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; });
  };

  const load = (markRead = true) => {
    if (!conversationId) return;
    api.get(`/chat/conversations/${conversationId}/messages`)
      .then((r) => {
        setMessages((prev) => {
          const next = r.data.messages || [];
          if (next.length !== prev.length || (next.length && prev.length && next[next.length - 1].id !== prev[prev.length - 1].id)) {
            scrollToBottom();
          }
          return next;
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    if (markRead) api.post(`/chat/conversations/${conversationId}/read`).then(() => { refreshUnread(); }).catch(() => {});
  };

  useEffect(() => {
    setLoading(true);
    setMessages([]);
    load();
    const t = setInterval(() => load(true), 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    setMentionState(activeMention(v, e.target.selectionStart || v.length));
  };

  const pickMention = (u) => {
    if (!mentionState) return;
    const before = text.slice(0, mentionState.at);
    const after = text.slice((taRef.current?.selectionStart) || text.length);
    const nv = `${before}@${u.name} ${after}`;
    setText(nv);
    setMentions((m) => (m.find((x) => x.id === u.id) ? m : [...m, { id: u.id, name: u.name }]));
    setMentionState(null);
    setTimeout(() => taRef.current?.focus(), 0);
  };

  const mentionMatches = useMemo(() => {
    if (!mentionState) return [];
    const qy = mentionState.query.toLowerCase();
    return users.filter((u) => u.id !== user?.id && (u.name || "").toLowerCase().includes(qy)).slice(0, 6);
  }, [mentionState, users, user]);

  const addAttach = (it) => setAttachments((a) => (a.find((x) => x.type === it.type && x.id === it.id) ? a : [...a, it]));

  const send = async () => {
    const body = text.trim();
    if (!body && attachments.length === 0) return;
    const activeMentions = mentions.filter((m) => body.includes(`@${m.name}`)).map((m) => m.id);
    setSending(true);
    try {
      const res = await api.post(`/chat/conversations/${conversationId}/messages`, { text: body, mentions: activeMentions, attachments });
      setMessages((m) => [...m, res.data]);
      setText(""); setMentions([]); setAttachments([]); setMentionState(null);
      scrollToBottom();
      onActivity?.();
      refreshUnread();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (mentionState && mentionMatches.length && (e.key === "Enter" || e.key === "Tab")) {
      e.preventDefault();
      pickMention(mentionMatches[0]);
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
    if (e.key === "Escape") setMentionState(null);
  };

  const showDefaultAttach = defaultAttach && !attachments.find((x) => x.type === defaultAttach.type && x.id === defaultAttach.id);

  return (
    <div className={`flex ${heightClass} flex-col`} data-testid="chat-thread">
      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
        {loading && <div className="flex h-full items-center justify-center text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…</div>}
        {!loading && messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
            <MessageSquare className="h-7 w-7 opacity-40" />
            {emptyHint || "No messages yet. Start the conversation."}
          </div>
        )}
        {messages.map((m, idx) => {
          const mine = m.sender_id === user?.id;
          const prev = messages[idx - 1];
          const showDaySep = !prev || dayKey(prev.created_at) !== dayKey(m.created_at);
          const grouped = prev && prev.sender_id === m.sender_id && !showDaySep;
          const names = (m.mentions || []).map((id) => userMap[id]?.name).filter(Boolean);
          return (
            <div key={m.id}>
              {showDaySep && (
                <div className="my-3 flex items-center gap-3" data-testid="chat-day-separator">
                  <div className="h-px flex-1 bg-border" />
                  <span className="rounded-full border border-border bg-secondary/60 px-3 py-0.5 text-[11px] font-semibold text-muted-foreground">{daySeparatorLabel(m.created_at)}</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
              )}
              <div className={`flex gap-3 ${grouped ? "mt-0.5" : "mt-3"}`} data-testid="chat-message">
                <div className="w-8">{!grouped && <Avatar name={m.sender_name} src={m.sender_avatar} />}</div>
                <div className="min-w-0 flex-1">
                  {!grouped && (
                    <div className="flex items-baseline gap-2">
                      <span className={`text-sm font-semibold ${mine ? "text-[hsl(var(--primary))]" : ""}`}>{m.sender_name}{mine && " (you)"}</span>
                      <span className="text-[11px] text-muted-foreground">{fmtTime(m.created_at)}</span>
                    </div>
                  )}
                  {m.text && <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">{renderText(m.text, names)}</div>}
                  {(m.attachments || []).map((a, i) => <ShareCard key={i} a={a} />)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-border p-3">
        {(attachments.length > 0 || showDefaultAttach) && (
          <div className="mb-2 flex flex-wrap gap-2">
            {attachments.map((a) => (
              <span key={`${a.type}-${a.id}`} className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/60 px-2 py-1 text-xs" data-testid={`chat-chip-${a.id}`}>
                {a.type === "quote" ? <FileText className="h-3 w-3" /> : <Building2 className="h-3 w-3" />}
                {a.label}
                <button onClick={() => setAttachments((x) => x.filter((y) => !(y.type === a.type && y.id === a.id)))}><X className="h-3 w-3" /></button>
              </span>
            ))}
            {showDefaultAttach && (
              <button onClick={() => addAttach(defaultAttach)} data-testid="chat-attach-default"
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-[hsl(var(--primary))]/50 px-2 py-1 text-xs text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10">
                <Plus className="h-3 w-3" /> Attach this {defaultAttach.type}
              </button>
            )}
          </div>
        )}
        <div className="relative flex items-end gap-2">
          {mentionState && mentionMatches.length > 0 && (
            <div className="absolute bottom-full left-10 z-30 mb-1 w-60 overflow-hidden rounded-lg border border-border bg-popover shadow-lg" data-testid="chat-mention-list">
              {mentionMatches.map((u) => (
                <button key={u.id} onClick={() => pickMention(u)} data-testid={`chat-mention-${u.id}`}
                  className="flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-secondary">
                  <Avatar name={u.name} src={u.avatar} size="h-6 w-6" />
                  <span className="truncate text-sm">{u.name}</span>
                  <span className="ml-auto text-[10px] uppercase text-muted-foreground">{u.role === "admin" ? "admin" : "rep"}</span>
                </button>
              ))}
            </div>
          )}
          <AttachPicker onAttach={addAttach} />
          <textarea
            ref={taRef}
            value={text}
            onChange={onChange}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder="Message…  (@ to mention, Enter to send)"
            data-testid="chat-input"
            className="max-h-32 min-h-[40px] flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-[hsl(var(--primary))]"
          />
          <Button onClick={send} disabled={sending || (!text.trim() && attachments.length === 0)} size="icon" className="h-10 w-10 shrink-0 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="chat-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
